import moment from 'moment';
import 'moment-timezone';
import type { KpiEnvelope } from '~stores/kpi-envelope';
import type { ResolvedPeriod } from './types';

/** The presets the API resolves itself (`period=` on every envelope route). */
export const API_PERIODS = [
    'today',
    '7d',
    '30d',
    '90d',
    'campaign',
    'last-week',
    'last-month',
] as const;

export type ApiPeriod = typeof API_PERIODS[number];

/** A preset as sent to the API: `campaign` needs the campaign it names. */
export type PeriodRequest =
    | { period: Exclude<ApiPeriod, 'campaign'> }
    | { period: 'campaign'; campaignId: string };

/** A campaign's inclusive `YYYY-MM-DD` bounds; no `endDate` while it runs. */
export interface CampaignWindow {
    startDate: string;
    endDate?: string;
}

const ANALYTICS_DAY_ZONE = 'America/Toronto';
const DATE_FORMAT = 'YYYY-MM-DD';

const day = (date: string) => moment.utc(date, DATE_FORMAT);
const iso = (date: moment.Moment) => date.format(DATE_FORMAT);

export const isApiPeriod = (value: unknown): value is ApiPeriod =>
    (API_PERIODS as readonly unknown[]).includes(value);

/** The day the API counts "yesterday" back from. */
export const analyticsToday = (now: Date = new Date()): string =>
    moment(now).tz(ANALYTICS_DAY_ZONE).format(DATE_FORMAT);

export const previousDay = (date: string): string =>
    iso(day(date).subtract(1, 'day'));

const trailing = (end: string, days: number): ResolvedPeriod => ({
    start: iso(day(end).subtract(days - 1, 'days')),
    end,
});

const weekBefore = (today: string): ResolvedPeriod => {
    const sunday = day(today).subtract(day(today).day(), 'days');
    return {
        start: iso(sunday.clone().subtract(7, 'days')),
        end: iso(sunday.subtract(1, 'day')),
    };
};

const monthBefore = (date: string): ResolvedPeriod => {
    const first = day(date).startOf('month').subtract(1, 'month');
    return { start: iso(first), end: iso(first.clone().endOf('month')) };
};

/** Same campaign rule as the API: its start through yesterday, or its end. */
export const campaignRange = (
    campaign: CampaignWindow,
    today: string
): ResolvedPeriod => {
    const yesterday = previousDay(today);
    const end =
        campaign.endDate && campaign.endDate < yesterday
            ? campaign.endDate
            : yesterday;
    return {
        start: campaign.startDate,
        end: end < campaign.startDate ? campaign.startDate : end,
    };
};

/**
 * The dates the API will resolve `request` to, for what the CMS shows before
 * the answer and for the windows it derives from it. Never sent beside it.
 */
export const resolvePeriodRequest = (
    request: PeriodRequest,
    today: string,
    campaign?: CampaignWindow
): ResolvedPeriod | undefined => {
    const yesterday = previousDay(today);
    const range = ((): ResolvedPeriod | undefined => {
        switch (request.period) {
            case 'today':
                return { start: today, end: today };
            case '7d':
                return trailing(yesterday, 7);
            case '30d':
                return trailing(yesterday, 30);
            case '90d':
                return trailing(yesterday, 90);
            case 'last-week':
                return weekBefore(today);
            case 'last-month':
                return monthBefore(today);
            case 'campaign':
                return campaign && campaignRange(campaign, today);
        }
    })();
    return range && { ...range, preset: request };
};

const COMPARE_LABELS: Record<ApiPeriod, string> = {
    today: 'from yesterday',
    '7d': 'from previous 7 days',
    '30d': 'from previous 30 days',
    '90d': 'from previous 90 days',
    'last-week': 'from previous week',
    'last-month': 'from previous month',
    campaign: 'from previous period',
};

/** The window the API compares `period` with (`compare=1`), and its label. */
export const comparePeriodFor = (
    period: ResolvedPeriod
): { range: ResolvedPeriod; label: string } => {
    const preset = period.preset?.period;
    const label = preset ? COMPARE_LABELS[preset] : 'from previous period';
    if (preset === 'last-week') {
        return {
            range: {
                start: iso(day(period.start).subtract(7, 'days')),
                end: iso(day(period.end).subtract(7, 'days')),
            },
            label,
        };
    }
    if (preset === 'last-month') {
        return { range: monthBefore(period.start), label };
    }
    const length = day(period.end).diff(day(period.start), 'days') + 1;
    return {
        range: {
            start: iso(day(period.start).subtract(length, 'days')),
            end: previousDay(period.start),
        },
        label,
    };
};

/** The query params that name `period` to the API: its preset, else its dates. */
export const periodQuery = (period: ResolvedPeriod): Record<string, string> => {
    const preset = period.preset;
    if (!preset) return { startDate: period.start, endDate: period.end };
    return preset.period === 'campaign'
        ? { period: preset.period, campaignId: preset.campaignId }
        : { period: preset.period };
};

/** The period and comparison the API actually answered for. */
export interface ServedPeriod {
    range: ResolvedPeriod;
    compareRange?: ResolvedPeriod;
}

export const servedPeriod = (
    env: Pick<KpiEnvelope, 'range' | 'compareRange'>
): ServedPeriod => ({
    range: { start: env.range.startDate, end: env.range.endDate },
    ...(env.compareRange && {
        compareRange: {
            start: env.compareRange.startDate,
            end: env.compareRange.endDate,
        },
    }),
});

/** The last day a hand-picked range may end on. */
export const lastSelectableDay = (now?: Date): string =>
    previousDay(analyticsToday(now));
